function clampByte(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function mixRgb(a, b, t) {
  return {
    r: clampByte(a.r + (b.r - a.r) * t),
    g: clampByte(a.g + (b.g - a.g) * t),
    b: clampByte(a.b + (b.b - a.b) * t),
  };
}

function scaleRgb(rgb, factor) {
  return {
    r: clampByte(rgb.r * factor),
    g: clampByte(rgb.g * factor),
    b: clampByte(rgb.b * factor),
  };
}

function hexToRgb(hex) {
  const normalized = String(hex).replace('#', '');
  if (normalized.length !== 6) return null;
  const value = Number.parseInt(normalized, 16);
  if (!Number.isFinite(value)) return null;
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

/**
 * Homepage hero cloud aura palette. Brand orange (key 'orange', no style vars
 * so the CSS default applies) plus four accent colors, all picked with equal
 * probability — no bias toward orange.
 */
export const HERO_AURA_COLORS = [
  'orange', // brand orange (CSS default vars)
  '#2dd36f', // green
  '#1d4ed8', // dark blue
  '#4dabf7', // light blue
  '#ff6b6b', // light red
];

/**
 * Accent hexes for the hero digit rain (brand orange is rendered via its own
 * digit tone, so it's excluded here).
 */
export const HERO_AURA_DIGIT_COLORS = HERO_AURA_COLORS.filter((key) => key !== 'orange');

/**
 * Pick the next homepage hero cloud aura step uniformly from `HERO_AURA_COLORS`,
 * avoiding an immediate repeat of `prevKey` so the cloud visibly alternates.
 */
export function pickHomeHeroAuraStep(prevKey = null) {
  const options = HERO_AURA_COLORS.filter((key) => key !== prevKey);
  const palette = options.length > 0 ? options : HERO_AURA_COLORS;
  const key = palette[Math.floor(Math.random() * palette.length)];
  if (key === 'orange') return { key: 'orange', vars: null };
  const rgb = hexToRgb(key);
  if (!rgb) return { key: 'orange', vars: null };
  return { key, vars: auraRgbToStyleVars(rgb) };
}

/** Parse `users/{uid}.auraColor` from Firestore. */
export function parseAuraRgb(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const { r, g, b } = raw;
  if (![r, g, b].every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  return { r: clampByte(r), g: clampByte(g), b: clampByte(b) };
}

/**
 * Resolve the stored aura color, or null when none is set. The selection
 * methodology is server-only (Cloud Functions); the client never generates or
 * derives a color, it only reads what the server assigned.
 */
export function resolveAuraRgb(storedRaw) {
  return parseAuraRgb(storedRaw);
}

function toRgb({ r, g, b }) {
  return `rgb(${r}, ${g}, ${b})`;
}

function toRgba({ r, g, b }, alpha) {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** CSS custom properties for HeroMatrixAura gradients and glows. */
export function auraRgbToStyleVars(rgb) {
  const light = mixRgb(rgb, { r: 255, g: 255, b: 255 }, 0.35);
  const mid = rgb;
  const dark = scaleRgb(rgb, 0.72);
  const darker = scaleRgb(rgb, 0.48);
  const darkest = scaleRgb(rgb, 0.28);
  const digitHighlight = mixRgb(rgb, { r: 255, g: 255, b: 255 }, 0.82);

  return {
    '--aura-rgb-light': toRgb(light),
    '--aura-rgb': toRgb(mid),
    '--aura-rgb-dark': toRgb(dark),
    '--aura-rgb-darker': toRgb(darker),
    '--aura-rgb-darkest': toRgb(darkest),
    '--aura-digit-highlight': toRgb(digitHighlight),
    '--aura-glow-soft': toRgba(mid, 0.7),
    '--aura-glow-mid': toRgba(light, 0.45),
    '--aura-glow-strong': toRgba(mid, 0.95),
    '--aura-glow-digit': toRgba(digitHighlight, 1),
    '--aura-icon-glow-1': toRgba(mid, 0.85),
    '--aura-icon-glow-2': toRgba(light, 0.65),
  };
}
